<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-grave-stone me-2"></i>{{ $t('page.funeral_home', 'Funeral Home') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-primary" @click="openAdmit">
            <i class="mdi mdi-plus me-1"></i>Admit Body
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setFilter('in_storage')" :class="{ selected: tab==='records' && statusFilter==='in_storage' }">
          <div class="hms-stat-num hms-stat-info">{{ countByStatus('in_storage') }}</div>
          <div class="hms-stat-lbl">In Storage</div>
        </div>
        <div class="hms-stat" @click="setFilter('released')" :class="{ selected: tab==='records' && statusFilter==='released' }">
          <div class="hms-stat-num hms-stat-green">{{ countByStatus('released') }}</div>
          <div class="hms-stat-lbl">Released</div>
        </div>
        <div class="hms-stat" @click="tab='unclaimed'" :class="{ selected: tab==='unclaimed' }">
          <div class="hms-stat-num hms-stat-amber">{{ unclaimedCount }}</div>
          <div class="hms-stat-lbl">Unclaimed</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-purple">{{ pmPendingCount }}</div>
          <div class="hms-stat-lbl">PM Pending</div>
        </div>
        <div class="hms-stat" @click="tab='storage'" :class="{ selected: tab==='storage' }">
          <div class="hms-stat-num">{{ availableCompartments }}/{{ storage.length }}</div>
          <div class="hms-stat-lbl">Compartments Free</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='records' }" @click="tab='records'">
          <i class="mdi mdi-clipboard-text me-1"></i>Records
        </button>
        <button class="hms-tab" :class="{ active: tab==='storage' }" @click="tab='storage'">
          <i class="mdi mdi-fridge-outline me-1"></i>Storage Map
        </button>
        <button class="hms-tab" :class="{ active: tab==='unclaimed' }" @click="tab='unclaimed'">
          <i class="mdi mdi-alert-octagon-outline me-1"></i>Unclaimed
          <span v-if="unclaimedCount" class="hms-tab-count">{{ unclaimedCount }}</span>
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ RECORDS TAB ============ -->
      <div v-else-if="tab === 'records'" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search body ref, name, NoK..." />
          </div>
          <select class="hms-input hms-filter" v-model="statusFilter">
            <option value="">All</option>
            <option value="in_storage">In Storage</option>
            <option value="released">Released</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="transferred">Transferred</option>
            <option value="buried_by_hospital">Buried by Hospital</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredRecords.length }} records</span>
        </div>

        <div v-if="filteredRecords.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Body Ref</th>
              <th>Name</th>
              <th>Gender / Age</th>
              <th>Date of Death</th>
              <th>Cause</th>
              <th>Slot</th>
              <th>Days</th>
              <th>Status</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in filteredRecords" :key="r.morgue_id" :class="rowClass(r)">
                <td><span class="hms-mono">{{ r.body_reference }}</span></td>
                <td>
                  <div class="hms-text fw-semibold">{{ r.full_name || 'Unidentified' }}</div>
                  <div class="hms-muted-sm" v-if="r.next_of_kin_name">NoK: {{ r.next_of_kin_name }}</div>
                </td>
                <td>{{ formatGender(r.gender) }}{{ r.estimated_age ? ' · ~' + r.estimated_age + 'y' : '' }}</td>
                <td>{{ formatDateTime(r.date_of_death) }}</td>
                <td><span class="hms-text-clip">{{ r.cause_of_death_primary || '—' }}</span></td>
                <td><span class="hms-mono">{{ r.storage_slot || '—' }}</span></td>
                <td>{{ daysInMorgue(r) }}d</td>
                <td><span class="hms-badge" :class="statusBadge(r.status)">{{ formatStatus(r.status) }}</span></td>
                <td>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(r)" title="View">
                    <i class="mdi mdi-arrow-right me-1"></i>Open
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-clipboard-text-outline"></i>No funeral home records.</div>
      </div>

      <!-- ============ STORAGE MAP ============ -->
      <div v-else-if="tab === 'storage'" class="hms-card">
        <div v-if="storage.length" class="hms-storage-grid">
          <div v-for="s in storage" :key="s.storage_id" class="hms-storage-cell" :class="storageClass(s.status)">
            <div class="hms-storage-num">{{ s.compartment_number }}</div>
            <div class="hms-storage-type">{{ formatCompartmentType(s.compartment_type) }}</div>
            <div class="hms-storage-status">
              <span class="hms-badge" :class="storageBadge(s.status)">{{ formatStorageStatus(s.status) }}</span>
            </div>
            <div v-if="s.body_reference" class="hms-storage-body">
              <i class="mdi mdi-account me-1"></i>
              <span class="hms-mono">{{ s.body_reference }}</span>
            </div>
            <div v-if="s.full_name" class="hms-muted-sm">{{ s.full_name }}</div>
            <div v-if="s.temperature_celsius" class="hms-muted-sm mt-2">
              <i class="mdi mdi-thermometer me-1"></i>{{ s.temperature_celsius }}°C
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-fridge-outline"></i>No storage compartments configured.</div>
      </div>

      <!-- ============ UNCLAIMED TAB ============ -->
      <div v-else-if="tab === 'unclaimed'" class="hms-card">
        <span class="hms-muted">{{ unclaimed.length }} unclaimed bodies</span>
        <div v-if="unclaimed.length" style="overflow-x:auto;" class="mt-3">
          <table class="hms-table">
            <thead><tr>
              <th>Body Ref</th>
              <th>Name</th>
              <th>Gender / Age</th>
              <th>Place of Death</th>
              <th>Slot</th>
              <th>Days in storage</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in unclaimed" :key="r.morgue_id" :class="unclaimedRowClass(r.days_in_morgue)">
                <td><span class="hms-mono">{{ r.body_reference }}</span></td>
                <td>{{ r.full_name || 'Unidentified' }}</td>
                <td>{{ formatGender(r.gender) }}{{ r.estimated_age ? ' · ~' + r.estimated_age + 'y' : '' }}</td>
                <td>{{ formatPlace(r.place_of_death) }}</td>
                <td><span class="hms-mono">{{ r.storage_slot || '—' }}</span></td>
                <td><strong :class="daysClass(r.days_in_morgue)">{{ r.days_in_morgue }}d</strong></td>
                <td>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(r)">
                    <i class="mdi mdi-arrow-right me-1"></i>Open
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty mt-3"><i class="mdi mdi-check-circle-outline"></i>No unclaimed bodies.</div>
      </div>

      <!-- ============ ADMIT BODY MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showAdmit" @click.self="showAdmit=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-2"></i>Admit Body to Funeral Home</span>
            <button class="hms-modal-close" @click="showAdmit=false">&times;</button>
          </div>
          <div v-if="admitError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ admitError }}</div>

          <!-- Patient picker (optional) -->
          <div class="mb-3">
            <label class="hms-label">Existing Patient (if known)</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search patient name, no..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.gender || '—' }}</div>
              </div>
            </div>
            <div v-if="admitForm.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>{{ admitForm.full_name }} (linked)
              <button class="hms-modal-close ms-auto" @click="clearPatient">&times;</button>
            </div>
            <div class="hms-muted-sm mt-1">Leave blank for unidentified body — fill name fields below.</div>
          </div>

          <div class="hms-section-label">DECEASED IDENTIFICATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Full Name (or "Unidentified")</label>
              <input class="hms-input" v-model="admitForm.full_name" placeholder="Surname First Middle, or Unidentified" />
            </div>
            <div class="col-md-3"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="admitForm.gender">
                <option value="unknown">Unknown</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div class="col-md-2"><label class="hms-label">Age (est)</label>
              <input type="number" class="hms-input" v-model.number="admitForm.estimated_age" min="0" max="150" />
            </div>
          </div>

          <div class="hms-section-label">DEATH DETAILS</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Date of Death</label>
              <input type="date" class="hms-input" v-model="admitForm.date_of_death" />
            </div>
            <div class="col-md-3"><label class="hms-label">Time</label>
              <input type="time" class="hms-input" v-model="admitForm.time_of_death" />
            </div>
            <div class="col-md-5"><label class="hms-label">Place of Death</label>
              <select class="hms-input" v-model="admitForm.place_of_death">
                <option value="ward">Ward</option>
                <option value="icu">ICU</option>
                <option value="casualty">Casualty</option>
                <option value="theatre">Theatre</option>
                <option value="on_arrival">On Arrival (BID)</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div class="col-md-7"><label class="hms-label">Primary Cause of Death</label>
              <input class="hms-input" v-model="admitForm.cause_of_death_primary" placeholder="e.g. Acute MI, Septic shock, RTA polytrauma" />
            </div>
            <div class="col-md-5"><label class="hms-label">Manner of Death</label>
              <select class="hms-input" v-model="admitForm.manner_of_death">
                <option value="natural">Natural</option>
                <option value="accident">Accident</option>
                <option value="homicide">Homicide</option>
                <option value="suicide">Suicide</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div class="col-md-7"><label class="hms-label">Attending Doctor</label>
              <select class="hms-input" v-model="admitForm.attending_doctor_id">
                <option value="">Select...</option>
                <option v-for="d in doctors" :key="d.user_id" :value="d.user_id">{{ d.first_name }} {{ d.last_name }}</option>
              </select>
            </div>
            <div class="col-md-5"><label class="hms-label">Police OB No. (optional)</label>
              <input class="hms-input" v-model="admitForm.police_ob_number" placeholder="e.g. OB 12/01/08/2026" />
            </div>
          </div>

          <div class="hms-section-label">STORAGE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Compartment *</label>
              <select class="hms-input" v-model="admitForm.storage_slot">
                <option value="">Select an available compartment...</option>
                <option v-for="s in availableSlots" :key="s.storage_id" :value="s.compartment_number">
                  {{ s.compartment_number }} ({{ formatCompartmentType(s.compartment_type) }})
                </option>
              </select>
            </div>
            <div class="col-md-5 d-flex align-items-end">
              <label class="hms-check"><input type="checkbox" v-model="admitForm.postmortem_required" :true-value="1" :false-value="0" /> Post-mortem required</label>
            </div>
          </div>

          <div class="hms-section-label">NEXT OF KIN</div>
          <div class="row g-3 mb-3">
            <div class="col-md-5"><label class="hms-label">Name</label>
              <input class="hms-input" v-model="admitForm.next_of_kin_name" />
            </div>
            <div class="col-md-4"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="admitForm.next_of_kin_phone" />
            </div>
            <div class="col-md-3"><label class="hms-label">Relationship</label>
              <input class="hms-input" v-model="admitForm.next_of_kin_relation" placeholder="Spouse, Father..." />
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doAdmit" :disabled="processing || !admitForm.storage_slot">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Admitting...' : 'Admit Body' }}
          </button>
        </div>
      </div>

      <!-- ============ DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDetail" @click.self="showDetail=false">
        <div class="hms-modal" v-if="detail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-clipboard-text me-2"></i>{{ detail.body_reference }}</span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Name</span><span>{{ detail.full_name || 'Unidentified' }}</span></div>
            <div><span class="hms-kv-key">Gender / Age</span><span>{{ formatGender(detail.gender) }}{{ detail.estimated_age ? ' · ' + detail.estimated_age + ' yrs' : '' }}</span></div>
            <div><span class="hms-kv-key">Date of Death</span><span>{{ formatDateTime(detail.date_of_death) }}</span></div>
            <div><span class="hms-kv-key">Place of Death</span><span>{{ formatPlace(detail.place_of_death) }}</span></div>
            <div><span class="hms-kv-key">Manner</span><span><span class="hms-badge" :class="mannerBadge(detail.manner_of_death)">{{ detail.manner_of_death }}</span></span></div>
            <div><span class="hms-kv-key">Primary Cause</span><span>{{ detail.cause_of_death_primary || '—' }}</span></div>
            <div v-if="detail.cause_of_death_secondary"><span class="hms-kv-key">Secondary Cause</span><span>{{ detail.cause_of_death_secondary }}</span></div>
            <div><span class="hms-kv-key">Attending Doctor</span><span>{{ detail.doctor_name || '—' }}</span></div>
            <div v-if="detail.police_ob_number"><span class="hms-kv-key">Police OB No.</span><span class="hms-mono">{{ detail.police_ob_number }}</span></div>
            <div><span class="hms-kv-key">Compartment</span><span class="hms-mono">{{ detail.storage_slot || '—' }}</span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="statusBadge(detail.status)">{{ formatStatus(detail.status) }}</span></span></div>
            <div v-if="detail.refrigeration_started"><span class="hms-kv-key">In Storage Since</span><span>{{ formatDateTime(detail.refrigeration_started) }}</span></div>
          </div>

          <div class="hms-section-label">NEXT OF KIN</div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Name</span><span>{{ detail.next_of_kin_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Phone</span><span>{{ detail.next_of_kin_phone || '—' }}</span></div>
            <div><span class="hms-kv-key">Relationship</span><span>{{ detail.next_of_kin_relation || '—' }}</span></div>
            <div><span class="hms-kv-key">Notified</span><span>{{ detail.kin_notified == 1 ? 'Yes — ' + formatDateTime(detail.kin_notified_at) : 'No' }}</span></div>
          </div>

          <div v-if="detail.postmortem_required == 1 || detail.postmortem_findings">
            <div class="hms-section-label">POST-MORTEM</div>
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Required</span><span>{{ detail.postmortem_required == 1 ? 'Yes' : 'No' }}</span></div>
              <div v-if="detail.postmortem_date"><span class="hms-kv-key">Date</span><span>{{ formatDateTime(detail.postmortem_date) }}</span></div>
              <div v-if="detail.postmortem_findings"><span class="hms-kv-key">Findings</span><span style="white-space:pre-wrap;">{{ detail.postmortem_findings }}</span></div>
            </div>
          </div>

          <div v-if="detail.released_at">
            <div class="hms-section-label">RELEASE</div>
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Released to</span><span>{{ detail.released_to }}</span></div>
              <div v-if="detail.released_to_id"><span class="hms-kv-key">ID Number</span><span class="hms-mono">{{ detail.released_to_id }}</span></div>
              <div v-if="detail.release_funeral_home"><span class="hms-kv-key">Funeral Home</span><span>{{ detail.release_funeral_home }}</span></div>
              <div><span class="hms-kv-key">Released At</span><span>{{ formatDateTime(detail.released_at) }}</span></div>
            </div>
          </div>

          <!-- Action panel -->
          <div class="d-flex gap-2 flex-wrap mt-3">
            <button v-if="detail.status === 'in_storage' && detail.postmortem_required == 1" class="hms-btn hms-btn-amber" @click="openPostmortem">
              <i class="mdi mdi-clipboard-pulse me-1"></i>{{ detail.postmortem_findings ? 'Update' : 'Record' }} Post-Mortem
            </button>
            <button v-if="detail.status === 'in_storage'" class="hms-btn hms-btn-primary" @click="openRelease">
              <i class="mdi mdi-export me-1"></i>Release Body
            </button>
          </div>
        </div>
      </div>

      <!-- ============ POSTMORTEM MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showPm" @click.self="showPm=false">
        <div class="hms-modal" style="max-width:600px;" v-if="detail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-clipboard-pulse me-2"></i>Post-Mortem Findings</span>
            <button class="hms-modal-close" @click="showPm=false">&times;</button>
          </div>
          <div v-if="pmError" class="hms-alert hms-alert-danger">{{ pmError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Body Ref</span><span class="hms-mono">{{ detail.body_reference }}</span></div>
            <div><span class="hms-kv-key">Name</span><span>{{ detail.full_name || 'Unidentified' }}</span></div>
          </div>

          <div class="mb-3"><label class="hms-label">PM Date</label>
            <input type="datetime-local" class="hms-input" v-model="pmForm.postmortem_date" />
          </div>
          <div class="mb-3"><label class="hms-label">Findings *</label>
            <textarea class="hms-input" rows="6" v-model="pmForm.postmortem_findings" placeholder="External examination, internal findings, conclusion..."></textarea>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doPostmortem" :disabled="processing || !pmForm.postmortem_findings">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Save Findings
          </button>
        </div>
      </div>

      <!-- ============ RELEASE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showRelease" @click.self="showRelease=false">
        <div class="hms-modal" style="max-width:540px;" v-if="detail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-export me-2"></i>Release Body</span>
            <button class="hms-modal-close" @click="showRelease=false">&times;</button>
          </div>
          <div v-if="releaseError" class="hms-alert hms-alert-danger">{{ releaseError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Body Ref</span><span class="hms-mono">{{ detail.body_reference }}</span></div>
            <div><span class="hms-kv-key">Name</span><span>{{ detail.full_name || 'Unidentified' }}</span></div>
            <div><span class="hms-kv-key">Compartment</span><span class="hms-mono">{{ detail.storage_slot || '—' }}</span></div>
          </div>

          <div class="mb-3"><label class="hms-label">Released to (Full name) *</label>
            <input class="hms-input" v-model="releaseForm.released_to" placeholder="Person collecting the body" />
          </div>
          <div class="mb-3"><label class="hms-label">National ID</label>
            <input class="hms-input" v-model="releaseForm.released_to_id" placeholder="ID number" />
          </div>
          <div class="mb-3"><label class="hms-label">Funeral Home (if applicable)</label>
            <input class="hms-input" v-model="releaseForm.release_funeral_home" />
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doRelease" :disabled="processing || !releaseForm.released_to">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Releasing...' : 'Confirm Release' }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-clip { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 150px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-green { color: var(--hms-success); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 8px; margin-bottom: 8px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; padding-top: 24px; }
.hms-check input { accent-color: var(--hms-primary); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }
.hms-row-overdue td  { background: rgba(220,53,69,.08); }
.hms-row-warning td  { background: rgba(245,158,11,.08); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }

.text-amber-strong { color: #f59e0b; font-weight: 700; }
.text-red-strong   { color: var(--hms-danger); font-weight: 700; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }

/* Storage map */
.hms-storage-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; }
.hms-storage-cell { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px; transition: all .15s; }
.hms-storage-cell.hms-storage-available { border-color: rgba(40,167,69,.4); background: rgba(40,167,69,.04); }
.hms-storage-cell.hms-storage-occupied  { border-color: rgba(155,89,182,.4); background: rgba(155,89,182,.04); }
.hms-storage-cell.hms-storage-maintenance { border-color: var(--hms-br); background: var(--hms-bg3); opacity: 0.7; }
.hms-storage-num { font-size: 18px; font-weight: 800; color: var(--hms-h); }
.hms-storage-type { color: var(--hms-s); font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
.hms-storage-status { margin-top: 8px; }
.hms-storage-body { margin-top: 8px; font-size: 13px; color: var(--hms-h); display: flex; align-items: center; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 150px; font-weight: 600; }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MorgueManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "records",
      search: "",
      statusFilter: "",

      records: [],
      storage: [],
      unclaimed: [],
      doctors: [],

      // Admit
      showAdmit: false,
      admitError: "",
      admitForm: this.emptyAdmit(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      // Detail
      showDetail: false,
      detail: null,

      // Postmortem
      showPm: false,
      pmError: "",
      pmForm: { postmortem_date: "", postmortem_findings: "" },

      // Release
      showRelease: false,
      releaseError: "",
      releaseForm: { released_to: "", released_to_id: "", release_funeral_home: "" },

      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    filteredRecords() {
      var list = this.records;
      if (this.statusFilter) list = list.filter(r => r.status === this.statusFilter);
      var q = this.search.trim().toLowerCase();
      if (q) list = list.filter(r => ((r.body_reference||"")+" "+(r.full_name||"")+" "+(r.next_of_kin_name||"")+" "+(r.cause_of_death_primary||"")).toLowerCase().indexOf(q) >= 0);
      return list;
    },
    availableSlots()       { return this.storage.filter(s => s.status === 'available'); },
    availableCompartments() { return this.availableSlots.length; },
    unclaimedCount()       { return this.unclaimed.length; },
    pmPendingCount()       { return this.records.filter(r => r.postmortem_required == 1 && !r.postmortem_findings && r.status === 'in_storage').length; }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyAdmit() {
      return {
        patient_id: null, full_name: "", gender: "unknown", estimated_age: 0,
        date_of_death: "", time_of_death: "", place_of_death: "ward",
        cause_of_death_primary: "", manner_of_death: "natural",
        attending_doctor_id: "", storage_slot: "",
        next_of_kin_name: "", next_of_kin_phone: "", next_of_kin_relation: "",
        postmortem_required: 0,
        police_ob_number: ""
      };
    },

    countByStatus(s) { return this.records.filter(r => r.status === s).length; },
    setFilter(s) { this.tab = "records"; this.statusFilter = s; },
    daysInMorgue(r) {
      if (!r.created_at) return 0;
      var dt = new Date(r.created_at); var now = new Date();
      return Math.floor((now - dt) / 86400000);
    },
    rowClass(r) {
      if (r.status !== 'in_storage') return "";
      var days = this.daysInMorgue(r);
      if (days >= 30) return "hms-row-overdue";
      if (days >= 14) return "hms-row-warning";
      return "";
    },
    unclaimedRowClass(d) {
      if (d >= 30) return "hms-row-overdue";
      if (d >= 14) return "hms-row-warning";
      return "";
    },
    daysClass(d) {
      if (d >= 30) return "text-red-strong";
      if (d >= 14) return "text-amber-strong";
      return "";
    },

    formatDate(d) { if (!d) return "—"; try { var dt=new Date(d); if(isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}); } catch(e){return d;} },
    formatDateTime(d) { if (!d) return "—"; try { var dt=new Date(d); if(isNaN(dt.getTime())) return d; return dt.toLocaleString(undefined,{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}); } catch(e){return d;} },
    formatGender(g) { return ({ male:'M', female:'F', unknown:'?' })[g] || g; },
    formatPlace(p) { return ({ ward:'Ward', icu:'ICU', casualty:'Casualty', theatre:'Theatre', on_arrival:'On Arrival', unknown:'Unknown' })[p] || p; },
    formatStatus(s) { return ({ in_storage:'In Storage', released:'Released', unclaimed:'Unclaimed', transferred:'Transferred', buried_by_hospital:'Buried by Hospital' })[s] || s; },
    formatStorageStatus(s) { return ({ available:'Available', occupied:'Occupied', maintenance:'Maintenance' })[s] || s; },
    formatCompartmentType(t) { return ({ standard:'Standard', infant:'Infant', isolation:'Isolation' })[t] || t; },
    statusBadge(s) {
      var m = { in_storage:'hms-badge-info', released:'hms-badge-success', unclaimed:'hms-badge-amber', transferred:'hms-badge-purple', buried_by_hospital:'hms-badge-neutral' };
      return m[s] || 'hms-badge-neutral';
    },
    storageBadge(s) {
      var m = { available:'hms-badge-success', occupied:'hms-badge-purple', maintenance:'hms-badge-neutral' };
      return m[s] || 'hms-badge-neutral';
    },
    storageClass(s) { return 'hms-storage-' + s; },
    mannerBadge(m) {
      switch(m) {
        case 'natural':  return 'hms-badge-neutral';
        case 'accident': return 'hms-badge-warning';
        case 'homicide':
        case 'suicide':  return 'hms-badge-danger';
        default:         return 'hms-badge-neutral';
      }
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadRecords(), this.loadStorage(), this.loadUnclaimed(), this.loadDoctors()]); }
      catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadRecords() {
      try { var r = await executeJson({ function: "getMorgueRecords", branch_id: this.branchId }, hms); if (r.data && r.data.success) this.records = (r.data.data && r.data.data.MorgueRecords) || []; } catch(e) {}
    },
    async loadStorage() {
      try { var r = await executeJson({ function: "getMorgueStorage", branch_id: this.branchId }, hms); if (r.data && r.data.success) this.storage = (r.data.data && r.data.data.Storage) || []; } catch(e) {}
    },
    async loadUnclaimed() {
      try { var r = await executeJson({ function: "getUnclaimedBodies", branch_id: this.branchId }, hms); if (r.data && r.data.success) this.unclaimed = (r.data.data && r.data.data.UnclaimedBodies) || []; } catch(e) {}
    },
    async loadDoctors() {
      try { var r = await executeJson({ function: "getDoctors", branch_id: this.branchId }, hms); if (r.data && r.data.success) this.doctors = (r.data.data && r.data.data.Doctors) || []; } catch(e) {}
    },

    // ===== ADMIT =====
    openAdmit() {
      this.admitForm = this.emptyAdmit();
      this.admitError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.showAdmit = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.admitForm.patient_id = p.patient_id;
      this.admitForm.full_name  = ((p.first_name || "") + " " + (p.last_name || "")).trim();
      this.admitForm.gender     = p.gender || "unknown";
      if (p.dob) {
        var dob = new Date(p.dob);
        var now = new Date();
        var age = now.getFullYear() - dob.getFullYear();
        if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age--;
        this.admitForm.estimated_age = age;
      }
      this.patientResults = []; this.patientSearch = "";
    },
    clearPatient() { this.admitForm.patient_id = null; this.admitForm.full_name = ""; this.admitForm.estimated_age = 0; },
    async doAdmit() {
      this.admitError = "";
      if (!this.admitForm.storage_slot) { this.admitError = "Pick an available compartment."; return; }
      this.processing = true;
      try {
        var payload = Object.assign({ function: "admitBody" }, this.admitForm);
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Body admitted: " + r.data.data.body_reference);
          this.showAdmit = false;
          await Promise.all([this.loadRecords(), this.loadStorage(), this.loadUnclaimed()]);
        } else { this.admitError = r.data.message || "Failed."; }
      } catch(e) { this.admitError = "Connection error."; }
      this.processing = false;
    },

    // ===== DETAIL =====
    openDetail(r) { this.detail = r; this.showDetail = true; },

    // ===== POSTMORTEM =====
    openPostmortem() {
      this.pmError = "";
      this.pmForm = {
        postmortem_date: this.detail.postmortem_date ? String(this.detail.postmortem_date).replace(' ','T').slice(0,16) : new Date().toISOString().slice(0,16),
        postmortem_findings: this.detail.postmortem_findings || ""
      };
      this.showPm = true;
    },
    async doPostmortem() {
      this.pmError = "";
      if (!this.pmForm.postmortem_findings) { this.pmError = "Findings are required."; return; }
      this.processing = true;
      try {
        var payload = {
          function: "recordPostmortem",
          morgue_id: this.detail.morgue_id,
          postmortem_findings: this.pmForm.postmortem_findings,
          postmortem_date: this.pmForm.postmortem_date ? this.pmForm.postmortem_date.replace('T', ' ') + ':00' : ''
        };
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Findings recorded");
          this.showPm = false;
          await this.loadRecords();
          // Refresh detail
          var fresh = this.records.find(x => x.morgue_id == this.detail.morgue_id);
          if (fresh) this.detail = fresh;
        } else { this.pmError = r.data.message || "Failed."; }
      } catch(e) { this.pmError = "Connection error."; }
      this.processing = false;
    },

    // ===== RELEASE =====
    openRelease() {
      this.releaseError = "";
      this.releaseForm = { released_to: "", released_to_id: "", release_funeral_home: "" };
      this.showRelease = true;
    },
    async doRelease() {
      this.releaseError = "";
      if (!this.releaseForm.released_to) { this.releaseError = "Released to is required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "releaseBody", morgue_id: this.detail.morgue_id }, this.releaseForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Body released");
          this.showRelease = false;
          this.showDetail = false;
          await Promise.all([this.loadRecords(), this.loadStorage(), this.loadUnclaimed()]);
        } else { this.releaseError = r.data.message || "Failed."; }
      } catch(e) { this.releaseError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
